import { Camera, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Modal } from "@/components/Modal";
import { TimeField } from "@/components/TimeField";
import {
  clockTimeOf,
  type DiaryMeal,
  type LabelIngredient,
  MEAL_TYPE_CATALOG,
  type MealType,
  suggestMealType,
} from "@/lib/diary";
import { estimateDiaryMeal, photoForEstimate } from "@/lib/estimateDiaryMeal";

interface Props {
  /** The meal being edited, or null for a new one. */
  initial: DiaryMeal | null;
  /** Run the estimate on the saved description as soon as the form opens. */
  autoEstimate?: boolean;
  onSave: (meal: DiaryMeal) => void;
  onClose: () => void;
}

interface Estimate {
  name: string;
  comment: string | null;
  ingredients: LabelIngredient[];
  // What it was made from. Once the text or the photo moves on, the
  // ingredients no longer belong to the meal and cannot be saved with it.
  description: string;
  photo: Photo | null;
}

interface Photo {
  mediaType: "image/jpeg";
  data: string;
  previewUrl: string;
}

export function MealDiaryModal({
  initial,
  autoEstimate = false,
  onSave,
  onClose,
}: Props) {
  const [time, setTime] = useState<string | undefined>(
    () => initial?.time ?? clockTimeOf(new Date()),
  );
  // A new meal's type follows its time until it is picked by hand; an edited
  // meal's type was already chosen and never moves on its own.
  const [pickedType, setPickedType] = useState<MealType | null>(
    initial?.type ?? null,
  );
  const [description, setDescription] = useState(initial?.description ?? "");
  // In memory only — photos are never stored (docs/meal-diary-design.md).
  const [photo, setPhoto] = useState<Photo | null>(null);
  const [estimate, setEstimate] = useState<Estimate | null>(
    initial?.name && initial.ingredients
      ? {
          name: initial.name,
          comment: null,
          ingredients: initial.ingredients,
          description: initial.description,
          photo: null,
        }
      : null,
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const photoInput = useRef<HTMLInputElement>(null);
  const mounted = useRef(true);
  useEffect(
    () => () => {
      mounted.current = false;
    },
    [],
  );

  const type = pickedType ?? (time ? suggestMealType(time) : "breakfast");
  const hasDescription = description.trim().length > 0;
  const current =
    estimate !== null &&
    estimate.description.trim() === description.trim() &&
    estimate.photo === photo;
  const canEstimate = !busy && (hasDescription || photo !== null);

  const runEstimate = async () => {
    if (!hasDescription && !photo) return;
    const madeFrom = { description, photo };
    setBusy(true);
    setError(null);
    try {
      const result = await estimateDiaryMeal({
        description,
        ...(photo
          ? { image: { mediaType: photo.mediaType, data: photo.data } }
          : {}),
      });
      if (!mounted.current) return;
      setEstimate({ ...result, ...madeFrom });
    } catch (err) {
      if (!mounted.current) return;
      setError(err instanceof Error ? err.message : "Fant ikke ingrediensene.");
    } finally {
      if (mounted.current) setBusy(false);
    }
  };

  // Hent ingredienser on a meal saved from text alone opens straight into
  // the estimate. Once, on open — never again on a later render.
  const autoStarted = useRef(false);
  useEffect(() => {
    if (!autoEstimate || autoStarted.current) return;
    autoStarted.current = true;
    void runEstimate();
  });

  const pickPhoto = async (file: File | undefined) => {
    if (!file) return;
    setError(null);
    try {
      setPhoto(await photoForEstimate(file));
    } catch {
      setError("Kunne ikke lese bildet.");
    }
  };

  const save = () => {
    if (!time) return;
    const base = {
      id: initial?.id ?? crypto.randomUUID(),
      time,
      type,
      description: description.trim(),
    };
    if (current && estimate) {
      onSave({
        ...base,
        name: estimate.name,
        ingredients: estimate.ingredients,
      });
    } else if (hasDescription) {
      // Saved from text alone: «Hent ingredienser» on the row runs the
      // estimate later.
      onSave(base);
    }
  };

  return (
    <Modal open onClose={onClose} data-testid="meal-diary-modal">
      <h2>{initial ? "Rediger måltid" : "Nytt måltid"}</h2>
      <div className="meal-diary-form">
        <label className="setting-label" htmlFor="meal-diary-time">
          Klokkeslett
        </label>
        <TimeField
          id="meal-diary-time"
          value={time}
          onChange={setTime}
          data-testid="meal-diary-time-input"
        />
        <fieldset className="meal-diary-types">
          <legend className="setting-label">Måltid</legend>
          <div className="meal-diary-type-buttons">
            {MEAL_TYPE_CATALOG.map((option) => (
              <button
                key={option.key}
                type="button"
                className={option.key === type ? "btn-primary" : "btn ghost"}
                aria-pressed={option.key === type}
                onClick={() => setPickedType(option.key)}
                data-testid={`meal-diary-type-${option.key}`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </fieldset>
        <label className="setting-label" htmlFor="meal-diary-description">
          Hva spiste du?
        </label>
        <textarea
          id="meal-diary-description"
          className="input"
          rows={4}
          placeholder="F.eks. «to brødskiver med brunost og et glass melk», eller ta et bilde"
          value={description}
          disabled={busy}
          onChange={(e) => setDescription(e.target.value)}
          data-testid="meal-diary-description-input"
        />
        <div className="meal-diary-photo">
          <input
            ref={photoInput}
            type="file"
            accept="image/*"
            capture="environment"
            hidden
            onChange={(e) => {
              void pickPhoto(e.target.files?.[0]);
              e.target.value = "";
            }}
            data-testid="meal-diary-photo-input"
          />
          {photo ? (
            <div className="meal-diary-photo-preview">
              <img
                src={photo.previewUrl}
                alt="Bilde av måltidet"
                data-testid="meal-diary-photo-preview"
              />
              <button
                type="button"
                className="btn ghost icon-btn"
                onClick={() => setPhoto(null)}
                disabled={busy}
                data-testid="meal-diary-photo-remove-button"
              >
                <X size={14} />
                Fjern bilde
              </button>
            </div>
          ) : (
            <button
              type="button"
              className="btn ghost icon-btn"
              onClick={() => photoInput.current?.click()}
              disabled={busy}
              data-testid="meal-diary-photo-button"
            >
              <Camera size={14} />
              Ta bilde
            </button>
          )}
        </div>

        {estimate ? (
          <div
            className={`meal-diary-estimate${current ? "" : " stale"}`}
            data-testid="meal-diary-estimate"
            data-current={current ? "true" : "false"}
          >
            <p className="meal-diary-estimate-name">{estimate.name}</p>
            {current && estimate.comment ? (
              <p className="hint" data-testid="meal-diary-comment">
                {estimate.comment}
              </p>
            ) : null}
            {current ? null : (
              <p className="hint" data-testid="meal-diary-stale-hint">
                Du har endret måltidet. Finn ingrediensene på nytt.
              </p>
            )}
            <details data-testid="meal-diary-ingredients">
              <summary>{estimate.ingredients.length} ingredienser</summary>
              <ul>
                {estimate.ingredients.map((ingredient, index) => (
                  <li
                    // biome-ignore lint/suspicious/noArrayIndexKey: read-only list, replaced wholesale on every estimate.
                    key={index}
                  >
                    {ingredient.name} · {Math.round(ingredient.grams)} g
                  </li>
                ))}
              </ul>
            </details>
            {current ? (
              <p className="hint">
                Noe som er feil? Rett det i teksten over og finn ingrediensene
                på nytt.
              </p>
            ) : null}
          </div>
        ) : null}

        {busy ? (
          <p className="hint" data-testid="meal-diary-busy">
            Finner ingrediensene …
          </p>
        ) : null}
        {error ? (
          <p className="field-error" data-testid="meal-diary-error">
            {error}
          </p>
        ) : null}
      </div>
      <div className="modal-actions">
        <button
          type="button"
          className="btn ghost"
          onClick={onClose}
          data-testid="meal-diary-cancel-button"
        >
          Avbryt
        </button>
        {current ? (
          <button
            type="button"
            className="btn-primary"
            disabled={busy || !time}
            onClick={save}
            data-testid="meal-diary-save-button"
          >
            Lagre
          </button>
        ) : (
          <>
            <button
              type="button"
              className="btn ghost"
              disabled={busy || !time || !hasDescription}
              onClick={save}
              data-testid="meal-diary-save-without-ingredients-button"
            >
              Lagre uten ingredienser
            </button>
            <button
              type="button"
              className="btn-primary"
              disabled={!canEstimate || !time}
              onClick={() => void runEstimate()}
              data-testid="meal-diary-estimate-button"
            >
              Finn ingredienser
            </button>
          </>
        )}
      </div>
    </Modal>
  );
}
